'use client';

import { Dialog, DialogPanel, TransitionChild } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { XMarkIcon, ClipboardIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/hooks/useDarkMode';
import { LaunchListDIYForm } from '@/components/common/LaunchListDIYForm';

type LaunchListPanelProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  keyId?: string;
  maxWidthClass?: string;
  description?: string;
};

export function LaunchListPanel(props: LaunchListPanelProps) {
  const {
    open,
    onClose,
    title = 'Sign up for Xronos AI',
    keyId = 'SWiZWj',
    maxWidthClass = 'max-w-xl',
    description = 'Join the waitlist to get early access!',
  } = props;

  const { isDarkMode } = useDarkMode();
  const [showSuccess, setShowSuccess] = useState(false);
  const [refId, setRefId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [lastSubmission, setLastSubmission] = useState<{ email: string; name?: string } | null>(null);
  const [reposting, setReposting] = useState(false);
  const [repostError, setRepostError] = useState<string | null>(null);

  // Reset state on open (fresh panel each time)
  useEffect(() => {
    if (open) {
      setShowSuccess(false);
      setRefId(null);
      setCopied(false);
      setRepostError(null);
      setReposting(false);
    }
  }, [open]);

  const referralOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://xronos.com';
  const referralUrl = refId ? `${referralOrigin}/?waitlist=1&ref=${encodeURIComponent(refId)}` : null;

  const handleCopy = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  interface LaunchListSubmitResponse {
    ref?: string | null;
    success?: boolean;
    url?: string;
    error?: string;
    existing?: boolean;
    [k: string]: unknown;
  }

  // Retry logic (rarely needed now, but retained for UX parity with commit)
  const handleRepost = async () => {
    if (!lastSubmission) return;
    setReposting(true);
    setRepostError(null);
    try {
      const fd = new FormData();
      fd.set('email', lastSubmission.email);
      if (lastSubmission.name) fd.set('name', lastSubmission.name);
      fd.set('api_key', keyId ?? '');
      const res = await fetch(`/api/launchlist/submit?keyId=${encodeURIComponent(keyId ?? '')}`, { method: 'POST', body: fd });
      let parsed: unknown = null;
      try { if ((res.headers.get('content-type') ?? '').includes('application/json')) parsed = await res.json(); } catch { /* ignore */ }
      if (!res.ok) { setRepostError('Retry failed.'); return; }
      const data: LaunchListSubmitResponse = parsed && typeof parsed === 'object' ? (parsed as LaunchListSubmitResponse) : {};
      if (data.ref ?? null) {
        setRefId(data.ref ?? null);
      } else if (data.error) {
        setRepostError(String(data.error));
      } else {
        setRepostError('No referral ID returned.');
      }
    } catch {
      setRepostError('Network issue.');
    } finally {
      setReposting(false);
    }
  };

  const shareButtons = referralUrl ? [
    {
      key: 'whatsapp',
      href: `https://api.whatsapp.com/send?text=${encodeURIComponent('Join me on the waitlist for Xronos Cloud: ' + referralUrl)}`,
      bg: 'bg-[#25D366]', hover: 'hover:brightness-110', ring: 'ring-white/10',
      icon: (<svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true" fill="currentColor"><path d="M12.04 2a9.94 9.94 0 0 0-8.54 15.07L2 22l5.13-1.47A9.95 9.95 0 1 0 12.04 2Zm0 18.16a8.17 8.17 0 0 1-4.16-1.15l-.3-.18-3.04.87.87-2.96-.2-.31a8.18 8.18 0 1 1 6.83 3.73ZM8.7 7.93c-.19-.42-.39-.43-.57-.43h-.49c-.17 0-.44.06-.67.31-.23.26-.88.86-.88 2.1 0 1.24.9 2.45 1.03 2.62.13.17 1.74 2.77 4.29 3.77 2.12.84 2.55.67 3.01.63.46-.04 1.48-.6 1.69-1.19.21-.58.21-1.08.15-1.19-.06-.1-.23-.16-.49-.28-.26-.13-1.48-.73-1.71-.81-.23-.09-.4-.13-.57.13-.17.26-.66.82-.81.98-.15.17-.3.19-.55.06-.26-.13-1.09-.4-2.07-1.27-.77-.68-1.29-1.52-1.44-1.78-.15-.26-.02-.4.11-.54.11-.11.26-.3.39-.45.13-.15.17-.26.26-.43.09-.17.04-.32-.02-.45-.06-.13-.53-1.3-.72-1.78Z" /></svg>),
      label: 'WhatsApp', aria: 'Share on WhatsApp'
    },
    {
      key: 'linkedin',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralUrl)}`,
      bg: 'bg-[#0A66C2]', hover: 'hover:brightness-110', ring: 'ring-white/10',
      icon: (<svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true" fill="currentColor"><path d="M4.98 3.5A2.5 2.5 0 1 0 5 8.5a2.5 2.5 0 0 0-.02-5ZM3 9.75h3.96v10.5H3Zm6.17 0H13v1.47h.06c.54-.97 1.9-1.99 3.9-1.99 4.17 0 4.94 2.66 4.94 6.12v4.9h-3.96v-4.34c0-1.03-.02-2.36-.72-3.16-.72-.83-1.73-.95-2.64-.73-1.25.3-2.1 1.38-2.1 3.43v4.8H9.17Z" /></svg>),
      label: 'LinkedIn', aria: 'Share on LinkedIn'
    },
    {
      key: 'reddit',
      href: `https://www.reddit.com/submit?url=${encodeURIComponent(referralUrl)}&title=${encodeURIComponent('Join me on the waitlist for Xronos Cloud!')}`,
      bg: 'bg-[#FF4500]', hover: 'hover:brightness-110', ring: 'ring-white/10',
      icon: (<svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true" fill="currentColor"><path d="M12 2c5.51 0 10 4.42 10 9.87 0 4.19-3.18 7.73-7.5 8.64v1.2a.87.87 0 0 1-1.3.76l-1.86-1.04-1.85 1.04a.87.87 0 0 1-1.3-.76v-1.2C5.18 19.6 2 16.06 2 11.87 2 6.42 6.49 2 12 2Zm3.77 5.62a1.2 1.2 0 1 0 .01 2.4 1.2 1.2 0 0 0-.01-2.4Zm-7.54 0a1.2 1.2 0 1 0 .01 2.4 1.2 1.2 0 0 0-.01-2.4ZM9.4 13.41c-.36-.2-.8.23-.57.57.54.77 1.72 1.28 3.17 1.28 1.45 0 2.63-.51 3.17-1.28.23-.34-.21-.77-.57-.57-.42.23-1.2.6-2.6.6-1.4 0-2.18-.37-2.6-.6Z" /></svg>),
      label: 'Reddit', aria: 'Share on Reddit'
    },
    {
      key: 'facebook',
      href: `https://www.facebook.com/sharer.php?u=${encodeURIComponent(referralUrl)}&quote=${encodeURIComponent('Join me on the waitlist for Xronos Cloud!')}`,
      bg: 'bg-[#3b5998]', hover: 'hover:brightness-110', ring: 'ring-white/10',
      icon: (<svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true" fill="currentColor"><path d="M12 2.04C6.5 2.04 2 6.53 2 12.06c0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47H15.2c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" /></svg>),
      label: 'Facebook', aria: 'Share on Facebook'
    }
  ] : [];

  return (
    <Dialog as="div" className="relative z-50" open={open} onClose={onClose}>
      <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
        <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-secondary/60'} backdrop-filter backdrop-grayscale overflow-y-auto`} aria-hidden="true">
          <div className="flex min-h-full items-start justify-center p-4">
            <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <DialogPanel className={`transform bg-secondary rounded-xl shadow-xl transition-all w-full ${maxWidthClass}`} style={{ position: 'relative', margin: '0 auto' }}>
                <div className={`${isDarkMode ? 'bg-card' : 'bg-card-accent'} rounded-xl relative px-6 py-8`}>
                  <button onClick={onClose} className={`absolute right-4 top-4 z-10 p-2 rounded-full shadow-lg opacity-70 hover:opacity-100 hover:scale-110 ${isDarkMode ? 'bg-gray-800 text-white border border-gray-700 hover:bg-gray-700 hover:text-gray-100' : 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-100 hover:text-black'}`} aria-label="Close panel">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                  <h2 className="text-2xl font-semibold text-primary mb-2">{title}</h2>
                  {description && <p className={`mb-3 text-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>}

                  {showSuccess ? (
                    <div>
                      {refId ? (
                        <div className={`rounded-lg border ${isDarkMode ? 'border-green-300/30 bg-green-900/20' : 'border-green-200 bg-green-50'} p-5`}>
                          <h3 className="text-lg font-semibold flex items-center gap-2"><span role="img" aria-hidden="true">🎉</span> Thanks for Registering!</h3>
                          <p className={`mt-3 text-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>We&apos;ll soon reach out to set you up on Xronos Cloud 🙌</p>
                          <p className={`mt-3 font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} text-base`}>Refer your friends and move up the list!</p>
                          <div className="mt-5 space-y-5">
                            <div>
                              <label className={`block text-xs font-medium mb-1 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Your referral link</label>
                              <div className={`group flex items-stretch rounded-lg border overflow-hidden shadow-sm backdrop-blur ${isDarkMode ? 'border-gray-600/70 bg-gray-800/60' : 'border-gray-200 bg-white/70'} transition`}>
                                <div className="flex-1 overflow-x-auto"><code className={`block px-3 py-3 text-sm whitespace-nowrap font-mono select-all ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{referralUrl}</code></div>
                                <button type="button" onClick={handleCopy} className={`px-4 py-3 flex items-center gap-1 text-sm font-medium border-l transition ${isDarkMode ? 'border-gray-600/70 bg-gray-700/60 hover:bg-gray-600 text-gray-100' : 'border-gray-200 bg-gray-50/70 hover:bg-gray-100 text-gray-700'}`} aria-label="Copy referral link">
                                  {copied ? (<><CheckIcon className="w-4 h-4 text-green-500" />Copied</>) : (<><ClipboardIcon className="w-4 h-4" />Copy</>)}
                                </button>
                              </div>
                            </div>
                            {referralUrl && (
                              <div>
                                <label className={`block text-xs font-medium mb-1 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Share</label>
                                <div className="flex flex-wrap gap-2">
                                  {shareButtons.map(btn => (
                                    <a key={btn.key} href={btn.href} target="_blank" rel="noopener noreferrer" aria-label={btn.aria} className={`${btn.bg} ${btn.hover} ${btn.ring} inline-flex items-center rounded-full px-4 py-2.5 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/20 transition shadow-sm`}>
                                      {btn.icon}<span className="ml-1">{btn.label}</span>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={`rounded-lg border ${isDarkMode ? 'border-red-400/30 bg-red-900/20' : 'border-red-200 bg-red-50'} p-5`}>
                          <h3 className="text-lg font-semibold flex items-center gap-2 text-red-600 dark:text-red-300"><ExclamationTriangleIcon className="w-5 h-5" />Registration Error</h3>
                          <p className={`mt-3 text-sm ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>Oops! Something went wrong while processing your registration.</p>
                          <div className="mt-4 flex gap-3 items-center flex-wrap">
                            <button onClick={handleRepost} disabled={reposting} className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${isDarkMode ? 'bg-gray-800 text-gray-100 hover:bg-gray-700 border border-gray-600 disabled:opacity-50' : 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-300 disabled:opacity-50'}`}>
                              {reposting && (<span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />)}
                              {reposting ? 'Retrying...' : 'Try Again'}
                            </button>
                            {repostError && (<p className="text-xs text-red-500 dark:text-red-300">{repostError}</p>)}
                            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-500 text-white">Close</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <LaunchListDIYForm
                      keyId={keyId}
                      onSuccess={() => setShowSuccess(true)}
                      onSuccessRef={(ref) => { setRefId(ref ?? null); setShowSuccess(true); }}
                      onCaptureSubmission={(email, name) => setLastSubmission({ email, name })}
                    />
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  );
}